import { useCallback, useMemo, useEffect, useLayoutEffect, useState } from 'react'
import { useNavigate, useLocation, useRouteError, isRouteErrorResponse } from 'react-router'
import Grid from '@mui/material/Grid'
import ActionPendingIcon from '@assets/images/action-pending.svg?react'
import DwightLogo from '../../assets/images/dwight-text.svg?react'
import Button from '../../components/Common/Button'
import Card from '../../components/Common/Card'
import Text from '../../components/Common/Text'
import { setCommittedLocationKey } from '../../helpers/committedLocation'
import { isLocalhost } from '../../helpers/helpers'
import styles from './ErrorPage.module.scss'

const isDevelopment = isLocalhost()

interface IDevErrorDetails {
  name?: string
  message?: string
  location?: string
  timestamp?: string | number
  stack?: string
}

// React Router Error Boundary Component
export const ErrorBoundary = () => {
  const error = useRouteError()
  const navigate = useNavigate()
  const { key } = useLocation()

  // Replaces the root element (NavigationSetup), so it reports the settled location instead;
  // otherwise the navigation progress bar would keep running over this page.
  useLayoutEffect(() => {
    setCommittedLocationKey(key)
  }, [key])

  useEffect(() => {
    // Store error details in sessionStorage for development
    if (error && isDevelopment) {
      let errorDetails

      if (isRouteErrorResponse(error)) {
        // Route error response (like 404s)
        errorDetails = {
          name: 'Route Error',
          message: `${error.status} ${error.statusText}: ${error.data}`,
          stack: '',
          location: window.location.href,
          timestamp: new Date().toISOString(),
        }
      } else if (error instanceof Error) {
        // Standard Error object
        errorDetails = {
          name: error.name || 'Component Error',
          message: error.message || 'A React component error occurred',
          stack: error.stack || '',
          location: window.location.href,
          timestamp: new Date().toISOString(),
        }
      } else {
        // Handle other types of errors
        errorDetails = {
          name: 'Unknown Error',
          message: typeof error === 'string' ? error : JSON.stringify(error),
          stack: '',
          location: window.location.href,
          timestamp: new Date().toISOString(),
        }
      }

      sessionStorage.setItem('dev_error_details', JSON.stringify(errorDetails))
    }

    // Clear the redirect flag if it exists
    if ((window as Window & { __REDIRECTING_TO_ERROR?: boolean }).__REDIRECTING_TO_ERROR) {
      delete (window as Window & { __REDIRECTING_TO_ERROR?: boolean }).__REDIRECTING_TO_ERROR
    }
  }, [error])

  const handleGoBack = useCallback(() => {
    void navigate(-1)
  }, [navigate])

  const handleGoHome = useCallback(() => {
    void navigate('/')
  }, [navigate])

  // Determine error type for display
  const isNotFound = isRouteErrorResponse(error) && error.status === 404
  const [devErrorDetails, setDevErrorDetails] = useState<IDevErrorDetails | null>(null)

  useEffect(() => {
    if (isDevelopment) {
      const errorDetails = sessionStorage.getItem('dev_error_details')
      if (errorDetails) {
        setDevErrorDetails(JSON.parse(errorDetails) as IDevErrorDetails)
        sessionStorage.removeItem('dev_error_details')
      }
    }

    return () => {
      setDevErrorDetails(null)
      sessionStorage.removeItem('dev_error_details')
    }
  }, [])

  return (
    <div className={styles.errorContainer}>
      <Card withBorder={false} className={styles.card}>
        <Grid container className={styles.content}>
          <Grid size={12}>
            <div className={styles.headingCenter}>
              <div>
                <DwightLogo className={styles.logo} />
              </div>

              <div className={styles.iconContainer} data-cy="error-container">
                <ActionPendingIcon className={styles.headingErrorIcon} />
              </div>

              <Text variant="pageTitle" tone="primary" className={styles.headingError}>
                {devErrorDetails && isDevelopment
                  ? '🚨 Development error'
                  : isNotFound
                    ? 'Page not found'
                    : 'Technical difficulties'}
              </Text>

              <Text
                variant="body"
                tone="tertiary"
                component="div"
                className={styles.headingInfoError}
              >
                {devErrorDetails && isDevelopment ? (
                  <div className={styles.devErrorContainer}>
                    <div className={styles.errorDetail}>
                      <strong>Error Name:</strong> {devErrorDetails.name}
                    </div>
                    <div className={styles.errorDetail}>
                      <strong>Message:</strong> {devErrorDetails.message}
                    </div>
                    <div className={styles.errorDetail}>
                      <strong>Location:</strong> {devErrorDetails.location}
                    </div>
                    <div className={styles.errorDetail}>
                      <strong>Time:</strong> {new Date(devErrorDetails.timestamp).toLocaleString()}
                    </div>
                    {devErrorDetails.stack && (
                      <div className={styles.stackTraceContainer}>
                        <strong>Stack Trace:</strong>
                        <pre className={styles.stackTrace}>{devErrorDetails.stack}</pre>
                      </div>
                    )}
                  </div>
                ) : isNotFound ? (
                  "The page you're looking for doesn't exist or has been moved. Let's get you back on track."
                ) : (
                  "We're experiencing some technical issues right now. Please try again in a moment or contact support if the problem persists."
                )}
              </Text>

              <div className={styles.buttonGroup}>
                {!isNotFound && (
                  <Button small={false} fullWidth onClick={handleGoBack}>
                    Go back
                  </Button>
                )}
                <Button small={false} secondary fullWidth onClick={handleGoHome}>
                  Return home
                </Button>
              </div>
            </div>
          </Grid>
        </Grid>
      </Card>
    </div>
  )
}

// Legacy ErrorPage component for auth routes that still need it
const ErrorPage = ({ source }: { source?: string }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const isFromIdValidator = useMemo(() => {
    return (
      new URLSearchParams(location.search).get('source') === 'invalid_id' || source === 'not_found'
    )
  }, [location.search, source])

  const handleGoBack = useCallback(() => {
    void navigate(-1)
  }, [navigate])

  const handleGoHome = useCallback(() => {
    void navigate('/')
  }, [navigate])

  return (
    <div className={styles.errorContainer}>
      <Card withBorder={false} className={styles.card} data-cy="error-card">
        <Grid container className={styles.content}>
          <Grid size={12}>
            <div className={styles.headingCenter}>
              <div>
                <DwightLogo className={styles.logo} />
              </div>

              <div className={styles.iconContainer}>
                <ActionPendingIcon className={styles.headingErrorIcon} />
              </div>

              <Text variant="pageTitle" tone="primary" className={styles.headingError}>
                {isFromIdValidator ? 'Page not found' : 'Technical difficulties'}
              </Text>

              <Text variant="body" tone="tertiary" className={styles.headingInfoError}>
                {isFromIdValidator
                  ? "The page you're looking for doesn't exist or has been moved. Let's get you back on track."
                  : "We're experiencing some technical issues right now. Please try again in a moment or contact support if the problem persists."}
              </Text>

              <div className={styles.buttonGroup}>
                {!isFromIdValidator && (
                  <Button small={false} fullWidth onClick={handleGoBack}>
                    Go back
                  </Button>
                )}
                <Button small={false} secondary fullWidth onClick={handleGoHome}>
                  Return home
                </Button>
              </div>
            </div>
          </Grid>
        </Grid>
      </Card>
    </div>
  )
}

export default ErrorPage
